/* The room a car stands in on the detail page.

   Every photograph the showroom files is a cut-out — a car on transparency,
   which is what a dealer's product shot is — so a plate behind one is not a
   background, it is a black box with a car floating in the middle of it.
   This is a room instead: night floor, the two plates the shoot is framed
   by, the neon ring the car stands inside, and a pool of lime under the sills.

   One component because the same room has to hold both a car that has been
   photographed and one still waiting for its shoot — moving between the two
   should not feel like the lighting changed.

   The layers are absolute, so the caller owns the frame: its aspect ratio,
   its radius, its border, and whatever stands on the floor. */

/* Where whatever stands on the floor belongs: concentric with the ring and
   held to a box the arc closes around, so the circle wraps the car instead
   of passing behind it. Exported because a photographed car and one still
   waiting for its shoot both stand in it, and the two have to be framed
   identically or navigating between them reads as the room moving. */
export const STAGE_SUBJECT =
  "absolute top-[49%] left-1/2 flex h-[50%] w-[52%] -translate-x-1/2 -translate-y-1/2 items-center justify-center";

function StudioStage({ uid }: { uid: string }) {
  return (
    <>
      {/* The ground, then the key light from above, then the geometry the
          hero sets its car against. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(180deg,#161616_0%,#080808_100%)]"
      />
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(55%_90%_at_50%_0%,rgb(255_255_255/0.07),transparent)]"
      />
      <span
        aria-hidden="true"
        className="absolute top-[12%] left-[6%] h-[48%] w-[30%] -rotate-12 rounded-[28px] border border-white/[0.05] bg-white/[0.02]"
      />
      <span
        aria-hidden="true"
        className="absolute top-[8%] right-[5%] h-[54%] w-[34%] rotate-[18deg] rounded-[32px] border border-white/[0.04] bg-white/[0.02]"
      />

      {/* Lime falling into the black from behind the ring, so the arc has
          something to sit in rather than a hard edge against the floor. */}
      <span
        aria-hidden="true"
        className="absolute top-[48%] left-1/2 h-[104%] w-[92%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgb(200_245_66/0.12),transparent)]"
      />

      {/* The neon ring the car stands inside — a soft halo under a bright
          gradient arc, turned so the gradient lifts from twelve o'clock.

          Drawn to very nearly the full height of the frame, because a ring
          that is to close around a car has to be wider than the car is: at
          the hero's smaller size the arc passed behind the wheels and the
          bonnet and read as a stripe rather than as a circle. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 200 200"
        className="absolute top-[48%] left-1/2 w-[60%] -translate-x-1/2 -translate-y-1/2 animate-neon"
      >
        <defs>
          <linearGradient id={`ring-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#e4ff7a" />
            <stop offset="55%" stopColor="#c8f542" />
            <stop offset="100%" stopColor="#9fd41a" />
          </linearGradient>
        </defs>
        <circle
          cx="100"
          cy="100"
          r="90"
          fill="none"
          stroke="rgb(200 245 66 / 0.16)"
          strokeWidth="16"
        />
        <circle
          cx="100"
          cy="100"
          r="90"
          fill="none"
          stroke={`url(#ring-${uid})`}
          strokeWidth="4.5"
          transform="rotate(-90 100 100)"
        />
      </svg>

      {/* The floor: a pool of lime light under the sills. It is what makes a
          shadow readable at all — on a near-black plate a shadow has nothing
          to fall across, which is the same reason a studio lights its sweep
          before it shoots the car. */}
      <span
        aria-hidden="true"
        className="absolute bottom-[4%] left-1/2 h-28 w-[70%] -translate-x-1/2 rounded-full bg-lime/25 blur-3xl"
      />
    </>
  );
}

/* The light the ring throws back onto the car. It has to be laid over the
   photograph rather than under it — the ring is behind the car, so what a
   visitor should see on the bodywork is its spill, not the arc itself. The
   caller renders it after whatever stands on the floor. */
export function StudioSpill() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(46%_46%_at_50%_48%,rgb(200_245_66/0.14),transparent_70%)] mix-blend-screen"
    />
  );
}

export default StudioStage;
